/**
 * Lightweight, Vale-style terminology / house-style linting.
 *
 * Pure and deterministic — no network, no LLM. Each rule is a regex (or plain
 * substring) scanned line-by-line over the markdown body, skipping fenced and
 * inline code so we don't flag identifiers or snippets. All findings are
 * advisory (`warn`); terminology never blocks publishing.
 */

export type TerminologyLevel = "warn";

export interface TerminologyRule {
  /** Stable identifier (kebab-case). */
  id: string;
  /** Severity — terminology is advisory only. */
  level: TerminologyLevel;
  /**
   * What to match. A string is treated as a case-insensitive literal substring;
   * a RegExp is used as-is (callers supply their own flags). Engine adds `g`
   * internally when needed, so omit `g` to keep rules readable.
   */
  pattern: string | RegExp;
  /** Human-readable note shown in the checklist. */
  message: string;
  /** Optional preferred phrasing. */
  suggestion?: string;
}

export interface TerminologyFinding {
  ruleId: string;
  level: TerminologyLevel;
  /** 1-based line number in the original markdown. */
  line: number;
  /** The exact text that matched. */
  match: string;
  message: string;
  suggestion?: string;
}

/**
 * Default ruleset tuned for index-methodology / financial-governance docs.
 * Deliberately conservative (a handful of high-signal rules) to avoid noise.
 * Disclaimer/section presence is intentionally out of scope — handled by the
 * section checks in publish-checks.ts.
 */
export const DEFAULT_RULES: readonly TerminologyRule[] = [
  // ── Weasel / filler phrasing ──
  {
    id: "filler-etc",
    level: "warn",
    pattern: /\betc\.?/i,
    message: 'avoid "etc." — enumerate the cases or scope them explicitly',
  },
  {
    id: "filler-and-so-on",
    level: "warn",
    pattern: /\band so on\b/i,
    message: 'avoid "and so on" — enumerate the cases explicitly',
  },
  {
    id: "weasel-obviously",
    level: "warn",
    pattern: /\bobviously\b/i,
    message: 'avoid "obviously" — state the reasoning instead of asserting it',
  },
  {
    id: "weasel-clearly",
    level: "warn",
    pattern: /\bclearly\b/i,
    message: 'avoid "clearly" — state the reasoning instead of asserting it',
  },
  {
    id: "weasel-simply",
    level: "warn",
    pattern: /\bsimply\b/i,
    message: 'avoid "simply" — it understates real complexity',
  },
  // ── House style: defined terms / capitalization ──
  {
    id: "cap-adjacent",
    level: "warn",
    // lowercase "adjacent" as a standalone word (the brand should be capitalized)
    pattern: /(?<![A-Za-z])adjacent(?![A-Za-z])/,
    message: 'capitalize "Adjacent" when referring to the brand',
    suggestion: "Adjacent",
  },
  {
    id: "cap-index-defined-term",
    level: "warn",
    // "the index" used as the defined term should be "the Index"
    pattern: /\bthe index\b/,
    message: 'capitalize "the Index" when used as the defined term',
    suggestion: "the Index",
  },
  {
    id: "cap-methodology-defined-term",
    level: "warn",
    // "this methodology" lowercase where the defined document is meant
    pattern: /\bthis methodology\b/,
    message: 'capitalize "this Methodology" when referring to this document',
    suggestion: "this Methodology",
  },
  {
    id: "style-rebalancing",
    level: "warn",
    // enforce "rebalancing" over the hyphenated "re-balancing"
    pattern: /\bre-balanc(e|ing|ed|es)\b/i,
    message: 'prefer "rebalancing" (no hyphen) for consistency',
    suggestion: "rebalancing",
  },
];

/**
 * Mask fenced code blocks (``` … ```) and inline code (`…`) so their contents
 * are not scanned. Masked characters are replaced with spaces to preserve line
 * numbers and column offsets exactly.
 */
function maskCode(markdown: string): string {
  const lines = markdown.split("\n");
  let inFence = false;
  const masked = lines.map((line) => {
    const fence = /^\s*(```|~~~)/.test(line);
    if (fence) {
      inFence = !inFence;
      return " ".repeat(line.length);
    }
    if (inFence) return " ".repeat(line.length);
    // Mask inline code spans within the line.
    return line.replace(/`[^`]*`/g, (m) => " ".repeat(m.length));
  });
  return masked.join("\n");
}

/** Build a global-flagged RegExp from a rule pattern (string → literal). */
function toRegExp(pattern: string | RegExp): RegExp {
  if (typeof pattern === "string") {
    const escaped = pattern.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
    return new RegExp(escaped, "gi");
  }
  const flags = pattern.flags.includes("g")
    ? pattern.flags
    : pattern.flags + "g";
  return new RegExp(pattern.source, flags);
}

/**
 * Scan `markdown` against `rules`, returning findings with 1-based line numbers
 * and the matched text. Code blocks and inline code are skipped.
 */
export function lintTerminology(
  markdown: string,
  rules: readonly TerminologyRule[] = DEFAULT_RULES,
): TerminologyFinding[] {
  if (!markdown) return [];
  const masked = maskCode(markdown);
  const lines = masked.split("\n");
  const findings: TerminologyFinding[] = [];

  for (let i = 0; i < lines.length; i++) {
    const line = lines[i];
    if (!line.trim()) continue;
    for (const rule of rules) {
      const re = toRegExp(rule.pattern);
      let m: RegExpExecArray | null;
      while ((m = re.exec(line)) !== null) {
        findings.push({
          ruleId: rule.id,
          level: rule.level,
          line: i + 1,
          match: m[0],
          message: rule.message,
          ...(rule.suggestion ? { suggestion: rule.suggestion } : {}),
        });
        // Guard against zero-width matches looping forever.
        if (m.index === re.lastIndex) re.lastIndex++;
      }
    }
  }

  return findings;
}
